import { StyleSheet } from "@react-pdf/renderer";
import type { CityVerdict } from "@/components/city-review/types";

/**
 * Centralized design tokens for the city-review PDF.
 *
 * Brand color is the Equip Sport forest green used in primary CTAs across the
 * app. Status palette is kept local (rather than imported from city-review
 * types) so the PDF feature has no soft dependencies on the surrounding web
 * report's evolving type module.
 *
 * Type scale follows a compressed print rhythm (10pt body) rather than the web
 * 14-16pt baseline, since A4 portrait at 36pt margins is much narrower than a
 * dashboard column.
 */

export type StatusKey = "approved" | "needs_change" | "rejected" | "pending";

const STATUS_HEX: Record<StatusKey, { solid: string; soft: string }> = {
  approved: { solid: "#15803d", soft: "#86efac" },
  needs_change: { solid: "#b45309", soft: "#fcd34d" },
  rejected: { solid: "#b91c1c", soft: "#fca5a5" },
  pending: { solid: "#9ca3af", soft: "#e5e7eb" },
};

export function verdictStatus(v: CityVerdict): StatusKey {
  switch (v) {
    case "approve":
      return "approved";
    case "needs_change":
      return "needs_change";
    case "reject":
      return "rejected";
    default:
      return "pending";
  }
}

const brand = "#1a8754";

const neutral = {
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  softBg: "#f8fafc",
  white: "#ffffff",
};

const status: Record<StatusKey, { solid: string; soft: string }> = STATUS_HEX;

const type = {
  h1: 24,
  h2: 16,
  h3: 12,
  body: 10,
  small: 9,
  tiny: 8,
};

const spacing = {
  xs: 4,
  sm: 6,
  md: 10,
  lg: 16,
  xl: 24,
};

/**
 * Restrained categorical palette for the sport distribution bar. Mirrors the
 * `QUIET_PALETTE` used by the on-screen pie chart so colors line up between
 * the PDF and the live report. Largest slice gets the brand green; the rest
 * cycle through this list.
 */
const sportPalette = [
  "#475569", // slate-600
  "#78716c", // stone-500
  "#64748b", // blue-gray
  "#a8a29e", // warm taupe
  "#5f7c7c", // dusty teal
  "#6b7280", // cool gray
  "#8a8275", // warm gray
  "#4b5e6b", // deep blue-gray
  "#9ca3af", // neutral gray
];

const unassignedSportColor = "#cbd5e1"; // slate-300

export const palette = {
  brand,
  text: neutral.text,
  muted: neutral.muted,
  border: neutral.border,
  softBg: neutral.softBg,
  white: neutral.white,
  status,
  type,
  spacing,
  sportPalette,
  unassignedSportColor,
};

/**
 * Top-level page and section styles. Page-specific layouts and per-component
 * styles live near their respective component to keep this file scannable.
 */
export const styles = StyleSheet.create({
  coverPage: {
    flexDirection: "column",
    padding: 56,
    fontFamily: "Helvetica",
    fontSize: type.body,
    color: neutral.text,
    backgroundColor: neutral.white,
  },
  contentPage: {
    flexDirection: "column",
    paddingTop: 40,
    paddingBottom: 56,
    paddingLeft: 36,
    paddingRight: 36,
    fontFamily: "Helvetica",
    fontSize: type.body,
    color: neutral.text,
    backgroundColor: neutral.white,
  },
  sectionTitle: {
    fontSize: type.h2,
    fontFamily: "Helvetica-Bold",
    color: neutral.text,
    marginBottom: spacing.md,
  },
  sectionSubtitle: {
    fontSize: type.small,
    color: neutral.muted,
    marginBottom: spacing.md,
  },
});
